import '@hotwired/turbo'

import { registerSharedCableCallbacks } from '@onpace/onspace-core/components/cable'

import translation from '@onpace/onspace-core/components/translations'
import OnspaceDialog from '@onpace/onspace-core/elements/dialog'

////////// Visits

let turboBeforeFetchCallback = null
let turboFetchForceProgress = false
let turboFetchProgressTimeout = null
let turboRenderCallback = null
let turboRenderLocation = null

document.addEventListener('turbo:click', (event) => {
  const action = event.target.getAttribute('data-turbo-action')
  const targetFrame = event.target.onspaceTurboTarget || document

  switch (action) {
  case 'dialog':
    turboBeforeFetchCallback = (visitEvent) => {
      const dialog = new OnspaceDialog()
      dialog.loadUrl(event.target.href)
      document.body.appendChild(dialog)

      visitEvent.preventDefault()
    }
    return
  case 'tooltip':
    turboBeforeFetchCallback = (visitEvent) => {
      const dialog = new OnspaceDialog({ tooltip: true })
      dialog.loadUrl(event.target.href)
      document.body.appendChild(dialog)

      visitEvent.preventDefault()
    }
    return
  case 'advance':
  case 'replace':
    if (targetFrame != document) {
      turboFetchForceProgress = true
    }
  }

  if (targetFrame.onspaceShouldPromptFormChanges) {
    turboBeforeFetchCallback = (visitEvent) => {
      const result = window.confirm(translation('onspace.navigation.form_unsaved.link'))
      if (!result) {
        visitEvent.preventDefault()
      }
    }
  }
})

document.addEventListener('turbo:before-prefetch', (event) => {
  event.preventDefault()
})

document.addEventListener('turbo:before-fetch-request', (event) => {
  if (turboBeforeFetchCallback) {
    const callback = turboBeforeFetchCallback
    turboBeforeFetchCallback = null

    callback(event)
  }

  if (event.target && event.target.getBooleanAttribute('data-turbo-progress')) {
    turboFetchForceProgress = true
  }

  if (turboFetchForceProgress) {
    const progressBar = window.Turbo.navigator.delegate.adapter.progressBar
    const progressDelay = window.Turbo.session.progressBarDelay
    progressBar.setValue(0)
    turboFetchProgressTimeout = window.setTimeout(() => progressBar.show(), progressDelay)
  }
})

document.addEventListener('turbo:before-fetch-response', (event) => {
  const dialogHeader = event.detail.fetchResponse.response.headers.get('Turbo-Open-Dialog')
  if (dialogHeader) {
    turboRenderCallback = (_renderEvent) => {
      const dialog = new OnspaceDialog()
      dialog.loadUrl(dialogHeader)
      document.body.appendChild(dialog)
    }
  }

  const locationHeader = event.detail.fetchResponse.response.headers.get('Turbo-Update-Location')
  if (locationHeader) { turboRenderLocation = locationHeader }

  if (turboFetchForceProgress) {
    turboFetchForceProgress = false
    window.clearTimeout(turboFetchProgressTimeout)
  }
})

document.addEventListener('turbo:render', (event) => {
  if (turboRenderCallback) {
    turboRenderCallback(event)
    turboRenderCallback = null
  }

  if (turboRenderLocation) {
    const location = new URL(turboRenderLocation)
    window.history.replaceState(window.history.state, null, location)
    turboRenderLocation = null
  }
})

document.addEventListener('turbo:frame-render', (event) => {
  if (turboRenderCallback) {
    turboRenderCallback(event)
    turboRenderCallback = null
  }
})

window.addEventListener('beforeunload', (event) => {
  if (document.onspaceShouldPromptFormChanges) {
    event.preventDefault()
    event.returnValue = translation('onspace.navigation.form_unsaved.link')
    return event.returnValue
  }
})

/// Retrieves the element's Turbo target, which will be either a Turbo Frame, or the document.
Object.defineProperty(HTMLElement.prototype, 'onspaceTurboTarget', {
  get: function() {
    const frameAttribute = this.getAttribute('data-turbo-frame')
    let frame = null
    if (frameAttribute === '_top') {
      return document
    } else if (typeof frameAttribute === 'string' && frameAttribute !== '_self') {
      frame = document.querySelector(`turbo-frame#${frameAttribute}`)
    }

    if (!frame) { frame = this.closest('turbo-frame') }

    if (frame) {
      return frame.onspaceTurboTarget
    } else {
      return document
    }
  }
})

/// Retrieves the element's Turbo target, which will be either another Turbo Frame, or the document.
const TurboFrame = window.customElements.get('turbo-frame')
Object.defineProperty(TurboFrame.prototype, 'onspaceTurboTarget', {
  get: function() {
    const targetAttribute = this.getAttribute('target')
    if (typeof targetAttribute === 'string') {
      if (targetAttribute === 'top') {
        return document
      } else {
        const frame = document.querySelector(`turbo-frame#${targetAttribute}`)
        if (frame) { return frame.onspaceTurboTarget }
      }
    }

    return this
  }
})

////////// Auto-Reloading

document.addEventListener('turbo:frame-load', (event) => {
  const reloadInterval = event.target.getIntegerAttribute('data-reload-interval')
  if (reloadInterval && reloadInterval > 0) {
    setTimeout(() => event.target.reload(), reloadInterval * 1000)
  }
})

////////// JS Execution

document.addEventListener('turbo:before-fetch-response', async (event) => {
  if (event.detail.fetchResponse.contentType.match(/^text\/javascript(;|$)/)) {
    const downloadUrl = event.detail.fetchResponse.response.headers.get('Turbo-Download')
    if (downloadUrl) {
      await turboDownload(downloadUrl)
    }

    // Safari will crash if we try to make page changes when there are open dialogs
    const dialog = event.target.closest('onspace-dialog')
    if (dialog) {
      await dialog.close({ ignoreForms: true })
    }

    event.preventDefault()
    event.detail.fetchResponse.responseText
      .then((responseText) => {
        (new Function(responseText))()
      })
  }
})

async function turboDownload(url) {
  return new Promise((resolve, _reject) => {
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.setAttribute('download', '')
    anchor.click()

    setTimeout(() => resolve(), 500)
  })
}

////////// Turbo-Streams

registerSharedCableCallbacks({
  messaged: (data) => {
    if (typeof data !== 'object' || typeof data.message !== 'object' || data.message.action !== 'onspace-turbo-stream') { return }
    const streamData = data.message.data
    if (Array.isArray(streamData)) {
      streamData.forEach((stream) => window.Turbo.renderStreamMessage(stream))
    } else {
      window.Turbo.renderStreamMessage(data.message.data)
    }
  }
})

document.addEventListener('turbo:before-stream-render', (event) => {
  const originalRender = event.detail.render
  event.detail.render = async (streamElement) => {
    await originalRender(streamElement)

    const afterEvent = new CustomEvent('turbo:after-stream-render', { bubbles: true, cancelable: false, detail: streamElement })
    streamElement.dispatchEvent(afterEvent)
  }
})
